import React from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LegacyImage as Image } from '@/atoms/LegacyImage'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { slugs } from '@/constants/slugs'
import { useThemeContext } from '@/contexts/ThemeContext'
import { useTranslate } from '@/utils/translate/translate-client'

export interface TheatricalFooterProps {
  mpaa?: {
    cloudinaryPath: string
    height: number
    width: number
  }
  billing?: {
    cloudinaryPath: string
    height: number
    width: number
  }
  includeAtom?: boolean
  includeFandango?: boolean
  slug?: string
}

export const TheatricalFooter: React.FC<TheatricalFooterProps> = ({
  mpaa,
  billing,
  includeAtom,
  includeFandango,
  slug,
}) => {
  const { isDarkMode } = useThemeContext()
  const { t } = useTranslate('theatrical-presales')

  return (
    <section
      className={classNames(
        'relative z-10',
        isDarkMode && slug === slugs.soundOfFreedom
          ? 'bg-core-gray-950 text-core-gray-100 py-16'
          : isDarkMode
          ? 'bg-core-gray-950 text-core-gray-100 py-8'
          : 'bg-white text-core-gray-950',
      )}
    >
      <PaddedContainer>
        <div className="flex flex-col items-center justify-between gap-8 text-center text-core-gray-500 sm:grid sm:grid-cols-2 sm:gap-2 lg:flex lg:flex-row">
          <When condition={slug === slugs.theShift}>
            <div className="flex w-full items-center justify-center">
              <a
                target="_blank"
                className="underline"
                href="https://www.sec.gov/edgar/search/#/ciks=0001779469&entityName=Shift%252C%2520LLC%2520%28C[…]ame=Shift%252C%2520LLC%2520%2520%28CIK%25200001779469%29"
              >
                Form C-AR filings for The Shift, LLC
              </a>
            </div>
          </When>
          <When condition={slug === slugs.gabrielAndTheGuardians}>
            <div className="flex w-full items-center justify-center">
              <a
                target="_blank"
                className="underline"
                href="https://www.sec.gov/edgar/search/#/ciks=0001991258&entityName=Qayinara%2520Animation%252C%2520Inc.%2520(CIK%25200001991258)"
              >
                Form C-AR filings for Qayinara Animation, Inc.
              </a>
            </div>
          </When>
          <When condition={slug === slugs.tuttleTwins}>
            <div className="flex w-full items-center justify-center">
              <a
                target="_blank"
                className="underline"
                href="https://www.sec.gov/edgar/search/#/ciks=0001826308&entityName=Tuttle%2520Twins%2520Show%252C%2520LLC%2520(CIK%25200001826308)"
              >
                Form C-AR filings for Tuttle Twins Show, LLC
              </a>
            </div>
          </When>
          <div className="order-2 mx-auto flex flex-col gap-2 sm:order-3 lg:order-2">
            {mpaa && (
              <Image
                layout="fixed"
                alt="MPAA"
                src={`${mpaa.cloudinaryPath}.webp`}
                height={mpaa.height}
                width={mpaa.width}
              />
            )}
            {billing && (
              <Image
                alt={t('billing', 'Billing')}
                layout="fixed"
                src={`${billing.cloudinaryPath}.webp`}
                height={billing.height}
                width={billing.width}
              />
            )}
          </div>
          {includeAtom && (
            <div className="order-3 mx-auto sm:order-2 lg:order-3">
              <Image
                alt="Atom Tickets"
                layout="fixed"
                src="/v1685994266/angel-studios/sound-of-freedom/atom.webp"
                height={56}
                width={173}
              />
            </div>
          )}
          {includeFandango && (
            <div className="order-4 mx-auto">
              <Image
                alt="Fandango"
                layout="fixed"
                src="/v1685994258/angel-studios/sound-of-freedom/fandango.webp"
                height={40}
                width={222}
              />
            </div>
          )}
        </div>
      </PaddedContainer>
    </section>
  )
}
